import React, { useEffect, useState } from "react";

import { Badge } from "Components/atoms/Badge";
import { Icon } from "Components/atoms/Icon";
import { Image } from "Components/atoms/Image";
import { useBreakpoint } from "shared/hooks/useBreakpoint";
import { theme } from "styles/theme";

import {
  CardContent,
  CardImageWrapper,
  CardTravelDetails,
  CardTravelInfo,
  CardTravelLocal,
  DesktopCardBody,
  DesktopCardFooter,
  DesktopCardFooterLine,
  DesktopCardHeading,
  DesktopCardText,
  PriceBanner,
  PriceDivider,
  PriceLabel,
  PriceValue,
  LineTravel,
  StyledCard,
} from "./styles";
import { CardCarouselProps } from "./types";

const parseCurrency = (value: string): number =>
  Number(value.replace(/[^\d,]/g, "").replace(",", "."));

const calculateDiscount = (price: string, averagePrice: string): number => {
  const offerValue = parseCurrency(price);
  const averageValue = parseCurrency(averagePrice);

  if (!offerValue || !averageValue || offerValue >= averageValue) return 0;

  return Math.round((1 - offerValue / averageValue) * 100);
};

const formatTravelDate = (value: string): string => {
  const [day, month, year] = value.trim().split("/").map(Number);

  if (!day || !month || !year) return value.trim();

  const months = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];

  return `${String(day).padStart(2, "0")} ${months[month - 1]} ${year}`;
};

const formatTravelDates = (value: string): string =>
  value.split("|").map(formatTravelDate).join(" - ");

/**
 * Componente CardCarousel
 *
 * Exibe um cartão interativo com informações de viagens, incluindo preço, trajeto e título.
 *
 * @param {string} image - URL da imagem exibida no cartão.
 * @param {string} price - Preço exibido no badge no canto inferior do cartão.
 * @param {string} title - Título do cartão exibido abaixo da trajetória.
 * @param {string} origem - Nome da cidade de origem.
 * @param {string} destino - Nome da cidade de destino.
 * @param {boolean} [isSelected=false] - Indica se o cartão está selecionado.
 */
export const CardCarousel: React.FC<CardCarouselProps> = ({
  image,
  price,
  averagePrice,
  travelDates,
  title,
  origem,
  destino,
  isSelected = false,
  desktopVariant = false,
  onCLick,
}) => {
  const [desktop, setDesktop] = useState(false);
  const breakpoint = useBreakpoint();

  useEffect(() => {
    if (breakpoint === "xxl") {
      setDesktop(true);
    } else {
      setDesktop(false);
    }
  }, [breakpoint]);

  const discount = averagePrice ? calculateDiscount(price, averagePrice) : 0;
  const hasFooterDetails = Boolean(averagePrice && travelDates);

  return (
    <StyledCard
      isSelected={isSelected}
      desktopVariant={desktopVariant}
      onClick={onCLick}
    >
      <CardImageWrapper isSelected={isSelected} desktopVariant={desktopVariant}>
        <Image
          src={image}
          alt={title}
          borderRadius={theme.borderRadius.medium}
        />
        {desktopVariant ? (
          <PriceBanner>
            <PriceLabel>Preço ida e volta</PriceLabel>
            <PriceDivider />
            <PriceValue>{price}</PriceValue>
          </PriceBanner>
        ) : (
          <Badge label={price} />
        )}
      </CardImageWrapper>

      {desktopVariant ? (
        <>
          <DesktopCardBody>
            <DesktopCardHeading>Voo para {destino}</DesktopCardHeading>
            <DesktopCardText>Saindo de {origem}</DesktopCardText>
          </DesktopCardBody>
          <DesktopCardFooter>
            {hasFooterDetails ? (
              <>
                <DesktopCardFooterLine>
                  {`(${discount > 0 ? `${discount}% desconto | ` : ""}normal ${averagePrice}+)`}
                </DesktopCardFooterLine>
                <DesktopCardFooterLine>
                  Datas: {formatTravelDates(travelDates!)}
                </DesktopCardFooterLine>
              </>
            ) : (
              <DesktopCardFooterLine>{title}</DesktopCardFooterLine>
            )}
          </DesktopCardFooter>
        </>
      ) : (
        <CardContent isSelected={isSelected}>
          <CardTravelInfo isSelected={isSelected}>Ida e Volta</CardTravelInfo>
          <LineTravel>
            <CardTravelLocal isSelected={isSelected}>{origem}</CardTravelLocal>
            <Icon
              library="cg"
              name="CgArrowsExchange"
              color={theme.colors.darkBlue}
              size={desktop ? "medium" : "small"}
            />
            <CardTravelLocal isSelected={isSelected}>{destino}</CardTravelLocal>
          </LineTravel>
          <CardTravelDetails>{title}</CardTravelDetails>
          <div />
        </CardContent>
      )}
    </StyledCard>
  );
};
